// src/mobile_pages/system/User_department.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from "../../api/users/departments";
import { listCompanies } from "../../api/users/companies";
import { Building2, ChevronDown, ChevronUp, Plus, Search, Layers } from "lucide-react";

const EMPTY_DEPT = { name: "", company: "" };

const User_department = () => {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [newDept, setNewDept] = useState({ ...EMPTY_DEPT });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ ...EMPTY_DEPT });
  const [loading, setLoading] = useState(true);

  // 검색 & 필터
  const [q, setQ] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  // 회사별 그룹
  const [grouped, setGrouped] = useState({});
  const [groupOpen, setGroupOpen] = useState({});

  // 등록 폼 표시
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadCompanies();
    loadDepartments();
    loadGroupedDepartments();
  }, []);

  useEffect(() => {
    loadDepartments();
  }, [q, companyFilter]);

  const loadCompanies = async () => {
    try {
      const res = await listCompanies();
      const data = res?.data ?? res;
      const rows = Array.isArray(data) ? data : data?.results ?? [];
      setCompanies(rows);
    } catch (error) {
      console.error("회사 목록 불러오기 실패:", error);
    }
  };

  const loadDepartments = async () => {
    setLoading(true);
    try {
      const res = await listDepartments({
        page: 1,
        page_size: 9999,
        search: q || undefined,
        company: companyFilter || undefined,
      });
      const data = res?.data ?? res;
      const list = Array.isArray(data) ? data : data?.results ?? [];
      setRows(list);
    } catch (e) {
      console.error("부서 목록 불러오기 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupedDepartments = async () => {
    try {
      const res = await listDepartments({
        page: 1,
        page_size: 9999,
        search: q || undefined,
      });
      const payload = res?.data ?? res;
      const all = Array.isArray(payload) ? payload : payload?.results ?? [];

      const map = {};
      all.forEach((r) => {
        const key =
          r.company_name ??
          (typeof r.company === "object" ? r.company?.name : r.company) ??
          "(회사없음)";
        if (!map[key]) map[key] = [];
        map[key].push(r);
      });
      setGrouped(map);

      const openInit = {};
      Object.keys(map).forEach((k) => (openInit[k] = true));
      setGroupOpen(openInit);
    } catch (e) {
      console.error("회사별 부서 로드 실패:", e);
    }
  };

  const handleCreate = async () => {
    if (!newDept.name.trim()) {
      alert("부서명을 입력하세요.");
      return;
    }
    if (!newDept.company) {
      alert("회사를 선택하세요.");
      return;
    }
    try {
      await createDepartment(newDept);
      setNewDept({ ...EMPTY_DEPT });
      setShowAddForm(false);
      await Promise.all([loadDepartments(), loadGroupedDepartments()]);
      alert("부서가 등록되었습니다.");
    } catch (e) {
      console.error("부서 등록 실패:", e);
      alert("부서 등록 실패");
    }
  };

  const handleUpdate = async (id) => {
    if (!editData.name.trim()) {
      alert("부서명을 입력하세요.");
      return;
    }
    if (!editData.company) {
      alert("회사를 선택하세요.");
      return;
    }
    try {
      await updateDepartment(id, editData);
      setEditId(null);
      await Promise.all([loadDepartments(), loadGroupedDepartments()]);
      alert("부서가 수정되었습니다.");
    } catch (e) {
      console.error("부서 수정 실패:", e);
      alert("부서 수정 실패");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await deleteDepartment(id);
      await Promise.all([loadDepartments(), loadGroupedDepartments()]);
      alert("부서가 삭제되었습니다.");
    } catch (e) {
      console.error("부서 삭제 실패:", e);
      alert("부서 삭제 실패");
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditData({
      name: row.name ?? "",
      company:
        typeof row.company === "object"
          ? row.company?.id ?? ""
          : row.company ?? "",
    });
  };

  // Skeleton Card
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-3 border animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
    </div>
  );

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-3 mobile-bottom-spacing">
        {/* Header */}
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-900">부서 관리</h2>
              <p className="text-[10px] text-gray-500 mt-0.5">전체 {rows.length}개</p>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="btn-create-sm flex items-center gap-1 text-xs"
            >
              <Plus className="w-3 h-3" />
              등록
            </button>
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="section-card p-3 space-y-3 animate-fade-in-soft">
            <p className="text-sm font-semibold">새 부서 등록</p>
            <input
              type="text"
              placeholder="부서명 *"
              value={newDept.name}
              onChange={(e) => setNewDept((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
            <select
              value={newDept.company}
              onChange={(e) => setNewDept((prev) => ({ ...prev, company: Number(e.target.value) }))}
              className="w-full border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">회사 선택 *</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button onClick={() => setShowAddForm(false)} className="btn-cancel-sm flex-1 text-xs">
                취소
              </button>
              <button onClick={handleCreate} className="btn-create-sm flex-1 text-xs">
                등록
              </button>
            </div>
          </div>
        )}

        {/* Search & Filter */}
        <div className="bg-white rounded-lg p-3 border space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="부서명 검색"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-md pl-8 pr-3 py-2 text-xs"
              />
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            </div>
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className={`btn-basic-sm text-xs px-3 ${filterOpen ? 'bg-blue-50 border-blue-300' : ''}`}
            >
              필터
            </button>
          </div>

          {filterOpen && (
            <div className="space-y-2 pt-2 border-t animate-fade-in-soft">
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs"
              >
                <option value="">전체 회사</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                onClick={() => { setQ(""); setCompanyFilter(""); }}
                className="btn-basic-sm text-xs w-full"
              >
                필터 초기화
              </button>
            </div>
          )}
        </div>

        {/* Department List */}
        <div className="space-y-2">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <Layers className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">등록된 부서가 없습니다.</p>
            </div>
          ) : (
            rows.map((r) =>
              editId === r.id ? (
                // Edit Mode Card
                <div key={r.id} className="section-card p-3 space-y-2">
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                  />
                  <select
                    value={editData.company}
                    onChange={(e) => setEditData((prev) => ({ ...prev, company: Number(e.target.value) }))}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="">회사 선택 *</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button onClick={() => setEditId(null)} className="btn-cancel-sm flex-1 text-xs">
                      취소
                    </button>
                    <button onClick={() => handleUpdate(r.id)} className="btn-save-sm flex-1 text-xs">
                      저장
                    </button>
                  </div>
                </div>
              ) : (
                // Normal Card
                <div key={r.id} className="section-card p-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{r.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      <Building2 className="w-3 h-3 inline mr-1" />
                      {r.company_name ?? "-"}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => startEdit(r)} className="btn-edit-sm text-xs">
                      수정
                    </button>
                    <button onClick={() => handleDelete(r.id)} className="btn-delete-sm text-xs">
                      삭제
                    </button>
                  </div>
                </div>
              )
            )
          )}
        </div>

        {/* Grouped by Company */}
        <div className="bg-white rounded-lg p-3 border space-y-2">
          <p className="text-sm font-semibold">회사별 부서 한눈에</p>

          {Object.keys(grouped).length === 0 ? (
            <p className="text-xs text-gray-500 py-2">데이터가 없습니다.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(grouped).map(([companyName, items]) => (
                <div key={companyName} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setGroupOpen((s) => ({ ...s, [companyName]: !s[companyName] }))}
                    className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
                  >
                    <span className="text-sm font-medium truncate">
                      {companyName}
                      <span className="text-gray-400 text-xs ml-1">({items.length})</span>
                    </span>
                    {groupOpen[companyName] ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  {groupOpen[companyName] && (
                    <div className="px-3 py-2 bg-white">
                      <div className="flex flex-wrap gap-1">
                        {items
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((d) => (
                            <span
                              key={d.id}
                              className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                            >
                              {d.name}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default User_department;
